import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmDialog } from './ConfirmDialog';
import api from '../utils/api';
import { getPublicPostShareUrl, shareLink } from '../utils/share';
import '../pages/Content.css';

interface Comment {
  id: number;
  comment_text: string;
  user_id: number;
  user_name: string;
  user_dp: string | null;
  like_count: number;
  liked_by_me: boolean;
  created_at: string;
}

interface Liker {
  id: number;
  name: string;
  profile_picture: string | null;
  liked_at: string;
}

export interface MediaInteractionCollaborator {
  id: number;
  role_name: string;
  user_id?: number;
  user_name: string;
  user_dp: string | null;
}

interface MediaInteractionModalProps {
  mediaId: number;
  src: string;
  isVideo: boolean;
  caption?: string | null;
  collaborators?: MediaInteractionCollaborator[];
  collaboratorsLoading?: boolean;
  canInteract?: boolean;
  onAuthRequired?: () => void;
  shareUrl?: string;
  shareTitle?: string;
  prefilledLikeCount?: number;
  prefilledCommentCount?: number;
  onRefreshSrc?: () => Promise<string | null>;
  onClose: () => void;
  portalZIndex?: number;
}

const MediaInteractionModal: React.FC<MediaInteractionModalProps> = ({
  mediaId,
  src,
  isVideo,
  caption,
  collaborators = [],
  collaboratorsLoading = false,
  canInteract = true,
  onAuthRequired,
  shareUrl,
  shareTitle,
  prefilledLikeCount = 0,
  prefilledCommentCount = 0,
  onRefreshSrc,
  onClose,
  portalZIndex,
}) => {
  const { user } = useAuth();
  const { confirm, notify, confirmDialog } = useConfirmDialog();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loaded, setLoaded] = useState(mediaId <= 0);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [likeCount, setLikeCount] = useState(prefilledLikeCount);
  const [likedByMe, setLikedByMe] = useState(false);
  const [showLikers, setShowLikers] = useState(false);
  const [likers, setLikers] = useState<Liker[]>([]);
  const [likersLoading, setLikersLoading] = useState(false);
  const [displaySrc, setDisplaySrc] = useState(src);
  const commentsEndRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const refreshingSrcRef = useRef(false);

  useEffect(() => {
    setDisplaySrc(src);
  }, [src]);

  const showInteractions = mediaId > 0;
  const hasCollaborators = collaboratorsLoading || collaborators.length > 0;
  const showInfoColumn = showInteractions || hasCollaborators || Boolean(caption);
  const shareTargetUrl = shareUrl || (mediaId > 0 ? getPublicPostShareUrl(mediaId) : '');
  const resolvedShareTitle = shareTitle || 'Check out this FilMini post';
  const displayedCommentCount = loaded ? comments.length : prefilledCommentCount;

  const loadMediaData = useCallback(async () => {
    try {
      setLoaded(false);
      setShowLikers(false);
      const [summaryRes, commentsRes] = await Promise.all([
        api.get(`/media/${mediaId}/likes-summary`),
        api.get(`/media/${mediaId}/comments`),
      ]);

      setLikeCount(summaryRes.data.like_count);
      setLikedByMe(summaryRes.data.liked_by_me);
      setComments(commentsRes.data.comments || []);
    } catch (err) {
      console.error('Failed to load media data', err);
    } finally {
      setLoaded(true);
    }
  }, [mediaId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';

    setLikeCount(prefilledLikeCount);
    setComments([]);

    if (showInteractions) {
      loadMediaData();
    } else {
      setLoaded(true);
    }

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [loadMediaData, onClose, prefilledLikeCount, showInteractions]);

  useEffect(() => {
    if (loaded) commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments.length, loaded]);

  const resizeComposer = useCallback(() => {
    const textarea = composerRef.current;
    if (!textarea) return;
    textarea.style.height = '0px';
    const nextHeight = Math.min(Math.max(textarea.scrollHeight, 48), 160);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > 160 ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    resizeComposer();
  }, [newComment, resizeComposer]);

  const handleLike = async () => {
    if (!canInteract) {
      onAuthRequired?.();
      return;
    }
    if (!showInteractions) return;
    try {
      const res = await api.post(`/media/${mediaId}/like`);
      setLikedByMe(res.data.liked);
      setLikeCount(res.data.like_count);
    } catch (err) {
      console.error('Like failed', err);
    }
  };

  const handleSubmitComment = async () => {
    if (!canInteract) {
      onAuthRequired?.();
      return;
    }
    if (!showInteractions || !newComment.trim() || submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/media/${mediaId}/comments`, { commentText: newComment.trim() });
      setComments(prev => [...prev, res.data.comment]);
      setNewComment('');
      setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      console.error('Comment post failed', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!canInteract) {
      onAuthRequired?.();
      return;
    }
    const approved = await confirm({
      title: 'Delete Comment',
      message: 'Delete this comment?',
      confirmLabel: 'Delete',
      cancelLabel: 'Keep',
      danger: true,
    });
    if (!approved) return;
    try {
      await api.delete(`/media/${mediaId}/comments/${commentId}`);
      setComments(prev => prev.filter(comment => comment.id !== commentId));
    } catch (err) {
      console.error('Delete comment failed', err);
    }
  };

  const handleCommentLike = async (commentId: number) => {
    if (!canInteract) {
      onAuthRequired?.();
      return;
    }
    try {
      const res = await api.post(`/media/${mediaId}/comments/${commentId}/like`);
      setComments(prev =>
        prev.map(comment =>
          comment.id === commentId
            ? { ...comment, liked_by_me: res.data.liked, like_count: res.data.like_count }
            : comment
        )
      );
    } catch (err) {
      console.error('Comment like failed', err);
    }
  };

  const handleShowLikers = async () => {
    if (!showInteractions) return;
    setShowLikers(true);
    setLikersLoading(true);
    try {
      const res = await api.get(`/media/${mediaId}/likes`);
      setLikers(res.data.likers || []);
    } catch (err) {
      console.error('Load likers failed', err);
    } finally {
      setLikersLoading(false);
    }
  };

  const handleShare = async () => {
    if (!shareTargetUrl) return;

    try {
      const outcome = await shareLink(shareTargetUrl, resolvedShareTitle);
      if (outcome === 'copied') {
        await notify({
          title: 'Link Copied',
          message: 'The share link has been copied to your clipboard.',
          confirmLabel: 'OK',
        });
      }
    } catch (error) {
      console.error('Share failed', error);
    }
  };

  const handleMediaError = async () => {
    if (!onRefreshSrc || refreshingSrcRef.current) return;
    refreshingSrcRef.current = true;
    try {
      const freshSrc = await onRefreshSrc();
      if (freshSrc && freshSrc !== displaySrc) {
        setDisplaySrc(freshSrc);
      }
    } catch (error) {
      console.error('Refresh media URL failed', error);
    } finally {
      refreshingSrcRef.current = false;
    }
  };

  const modal = createPortal(
    <div
      className="cf-modal-overlay shared-media-modal-overlay"
      style={portalZIndex ? { zIndex: portalZIndex } : undefined}
      onClick={onClose}
    >
      <div
        className={`cf-modal shared-media-modal ${!showInfoColumn ? 'shared-media-modal--media-only' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        <button className="cf-modal-close" onClick={onClose}>✕</button>

        <div className="cf-modal-media">
          {isVideo ? (
            <video
              className="cf-modal-media-el"
              controls
              autoPlay
              playsInline
              src={displaySrc}
              onError={handleMediaError}
              onContextMenu={e => e.preventDefault()}
              controlsList="nodownload"
            />
          ) : (
            <img
              className="cf-modal-media-el"
              src={displaySrc}
              alt=""
              onError={handleMediaError}
              onContextMenu={e => e.preventDefault()}
              draggable={false}
            />
          )}
        </div>

        {showInfoColumn && (
          <div className="cf-modal-info">
            {caption && <p className="cf-modal-caption">{caption}</p>}

            {hasCollaborators && (
              <div className="shared-media-modal-collabs">
                <div className="cp-collabs-label">Collaborators</div>
                {collaboratorsLoading ? (
                  <div className="cp-collabs-empty">Loading...</div>
                ) : collaborators.length === 0 ? (
                  <div className="cp-collabs-empty">No collaborators tagged</div>
                ) : (
                  <div className="cp-collabs-list">
                    {collaborators.map(collaborator => (
                      <div key={collaborator.id} className="cp-collab-chip">
                        <div className="cp-collab-avatar">
                          {collaborator.user_dp ? (
                            <img src={collaborator.user_dp} alt={collaborator.user_name} />
                          ) : (
                            collaborator.user_name.charAt(0).toUpperCase()
                          )}
                        </div>
                        <div className="cp-collab-info">
                          <span className="cp-collab-name">{collaborator.user_name}</span>
                          <span className="cp-collab-role">{collaborator.role_name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {showInteractions && (
              <>
                <div className="cf-modal-likebar">
                  <button
                    className={`cf-like-btn ${likedByMe ? 'cf-like-btn--active' : ''}`}
                    onClick={handleLike}
                    aria-label={likedByMe ? 'Unlike' : 'Like'}
                  >
                    <span className="cf-heart-icon">{likedByMe ? '♥' : '♡'}</span>
                  </button>
                  <button className="cf-like-count-btn" onClick={handleShowLikers}>
                    {likeCount} like{likeCount === 1 ? '' : 's'}
                  </button>
                  <span className="cf-comment-count">
                    {displayedCommentCount} comment{displayedCommentCount !== 1 ? 's' : ''}
                  </span>
                  <button className="cf-like-count-btn" onClick={handleShare}>
                    <span aria-hidden="true">🔗</span> Share
                  </button>
                </div>

                {!canInteract ? (
                  <p className="cf-no-comments">Log in to like or comment on this post.</p>
                ) : null}

                <div className="cf-modal-comments">
                  {showLikers ? (
                    likersLoading ? (
                      <div className="cf-comments-loading">Loading...</div>
                    ) : likers.length === 0 ? (
                      <p className="cf-no-comments">No likes yet.</p>
                    ) : (
                      <div className="cf-likers-list">
                        {likers.map(liker => (
                          <div key={liker.id} className="cf-liker-row">
                            <div className="cf-liker-avatar">
                              {liker.profile_picture ? (
                                <img src={liker.profile_picture} alt={liker.name} />
                              ) : (
                                <span>{liker.name.charAt(0).toUpperCase()}</span>
                              )}
                            </div>
                            <span className="cf-liker-name">{liker.name}</span>
                          </div>
                        ))}
                      </div>
                    )
                  ) : !loaded ? (
                    <div className="cf-comments-loading">Loading...</div>
                  ) : comments.length === 0 ? (
                    <p className="cf-no-comments">No comments yet. Be the first!</p>
                  ) : (
                    comments.map(comment => (
                      <div key={comment.id} className="cf-comment">
                        <div className="cf-comment-avatar">
                          {comment.user_dp ? (
                            <img src={comment.user_dp} alt={comment.user_name} />
                          ) : (
                            <span>{comment.user_name.charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="cf-comment-body">
                          <span className="cf-comment-author">{comment.user_name}</span>
                          <span className="cf-comment-text">{comment.comment_text}</span>
                        </div>
                        <div className="cf-comment-actions">
                          <button
                            className={`cf-clike-btn ${comment.liked_by_me ? 'cf-clike-btn--active' : ''}`}
                            onClick={() => handleCommentLike(comment.id)}
                          >
                            {comment.liked_by_me ? '♥' : '♡'}{comment.like_count > 0 ? ` ${comment.like_count}` : ''}
                          </button>
                          {comment.user_id === user?.id && (
                            <button className="cf-cdel-btn" onClick={() => handleDeleteComment(comment.id)}>×</button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={commentsEndRef} />
                </div>

                {canInteract ? (
                  <div className="cf-comment-input-row">
                    <textarea
                      ref={composerRef}
                      className="cf-comment-input"
                      placeholder="Add a comment..."
                      value={newComment}
                      onChange={e => setNewComment(e.target.value)}
                      onInput={resizeComposer}
                      maxLength={500}
                      rows={1}
                    />
                    <button
                      className="cf-comment-submit"
                      onClick={handleSubmitComment}
                      disabled={!newComment.trim() || submitting}
                    >
                      {submitting ? '...' : 'Post'}
                    </button>
                  </div>
                ) : (
                  <div className="cf-comment-input-row">
                    <textarea
                      className="cf-comment-input"
                      placeholder="Log in to add a comment..."
                      value=""
                      readOnly
                      rows={1}
                      onFocus={() => onAuthRequired?.()}
                    />
                    <button
                      className="cf-comment-submit"
                      onClick={() => onAuthRequired?.()}
                    >
                      Login
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );

  return (
    <>
      {confirmDialog}
      {modal}
    </>
  );
};

export default MediaInteractionModal;
